import { useState } from 'react';

import { ExternalLink } from '@/components/blocks/links/ExternalLink';
import { InternalLink } from '@/components/blocks/links/InternalLink';
import { useAuth } from '@/hooks/useAuth';

import googleIcon from '@/icons/google.svg';
import yelpIcon from '@/icons/yelp.svg';
import zillowIcon from '@/icons/zillow.svg';

import { reviewLinks, socialLinks } from './data/footer-data';
import { googlePages } from './data/google-data';
import type { ReviewPage } from './data/review-page';
import { yelpPages } from './data/yelp-data';
import { zillowPages } from './data/zillow-data';
import { FooterLegal } from './FooterLegal';
import { ReviewModal } from './ReviewModal';

const accountCenterUrl = process.env.NEXT_PUBLIC_ACCOUNT_CENTER_URL;

interface ActiveReviewModal {
  pages: ReviewPage[];
  title: string;
  icon: string;
  iconAlt: string;
}

const reviewModals: Record<string, ActiveReviewModal> = {
  Zillow: {
    pages: zillowPages,
    title: 'Choose a Zillow Page',
    icon: zillowIcon.src,
    iconAlt: 'Zillow',
  },
  Yelp: { pages: yelpPages, title: 'Choose a Yelp Page', icon: yelpIcon.src, iconAlt: 'Yelp' },
  Google: {
    pages: googlePages,
    title: 'Choose a Google Page',
    icon: googleIcon.src,
    iconAlt: 'Google',
  },
};

const linkClass = 'text-footer text-base hover:text-footer-text-hover';

export const FooterMobile = () => {
  const { isAuthenticated } = useAuth();
  const softwareSupportHref = isAuthenticated
    ? 'https://marshallreddick.atlassian.net/servicedesk/customer/portals'
    : `${accountCenterUrl}/login`;

  const [activeModal, setActiveModal] = useState<ActiveReviewModal | null>(null);

  const allIcons = [
    ...reviewLinks.map(({ text, href, icon }) => {
      const modal = reviewModals[text];
      if (modal) {
        return (
          <button key={text} onClick={() => setActiveModal(modal)}>
            <img src={icon} alt={text} className="w-6 h-6" />
          </button>
        );
      }
      return (
        <a key={text} href={href} target="_blank" rel="noopener noreferrer">
          <img src={icon} alt={text} className="w-6 h-6" />
        </a>
      );
    }),
    ...socialLinks.map(({ text, href, icon, noreferrer }) => (
      <a
        key={text}
        href={href}
        target="_blank"
        rel={noreferrer === false ? 'noopener' : 'noopener noreferrer'}
      >
        <img src={icon} alt={text} className="w-6 h-6" />
      </a>
    )),
  ];

  return (
    <footer className="relative z-20 bg-footer-bg border-t-[3px] border-footer-border p-2.5 text-center text-footer text-sm">
      <div className="space-y-1.5 text-sm">
        <div className="m-0">
          <InternalLink to="/contact" text="Contact Us" className={linkClass} />
          {' - '}
          <InternalLink to="/about" text="About Us" className={linkClass} />
          {' - '}
          <InternalLink to="/careers" text="Careers" className={linkClass} />
          {' - '}
          <ExternalLink
            href="https://www.markful.com/apparel-841107?CompanyId=604782"
            text="Shop"
            className={linkClass}
          />
        </div>
        <div>
          <InternalLink to="/preferred-vendors" text="Preferred Vendors" className={linkClass} />
        </div>
        <div>
          <InternalLink to="/learn/faq" text="FAQ" className={linkClass} />
          {' - '}
          <ExternalLink
            href={softwareSupportHref}
            text="Software Support"
            className={linkClass}
          />{' '}
          <InternalLink to="/privacy-policy" text="Privacy Policy" className={linkClass} />
          {' - '}
          <br />
          <InternalLink
            to="/terms-and-conditions"
            text="Terms and Conditions"
            className={linkClass}
          />
        </div>
      </div>

      <div className="flex justify-center gap-1.5 mt-2 mb-2">{allIcons}</div>

      <div className="px-3.75">
        <FooterLegal
          paragraphClassName="text-xs text-footer leading-5.5 space-y-6 py-1.5 text-center"
          trecLinkClassName="text-footer hover:text-footer-text-hover underline block leading-5"
          splitCopyright
          copyrightClassName="py-2 pb-6 text-xs text-footer"
        />
      </div>

      {activeModal && (
        <ReviewModal
          pages={activeModal.pages}
          title={activeModal.title}
          icon={activeModal.icon}
          iconAlt={activeModal.iconAlt}
          onClose={() => setActiveModal(null)}
        />
      )}
    </footer>
  );
};
